import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { AppLayout } from "@/components/AppLayout";
import {
  Users, Plus, Copy, RefreshCw, TrendingUp, TrendingDown, Minus,
  UserPlus, Trash2, Eye, EyeOff, ShieldCheck, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { cn } from "@/lib/utils";

const TREND_COLORS = { improving: "text-green-500", declining: "text-orange-500", stable: "text-muted-foreground" };
const TREND_ICONS = { improving: TrendingUp, declining: TrendingDown, stable: Minus };

export default function FamilyPage() {
  const { token, user } = useAuth();
  const [family, setFamily] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [familyName, setFamilyName] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [newMember, setNewMember] = useState({ username: "", password: "", preferred_name: "", date_of_birth: "", role: "member" });
  const [addingMember, setAddingMember] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  const headers = { Authorization: `Bearer ${token}` };
  const isGuardian = family?.guardian_id === user?.id;

  useEffect(() => { fetchFamily(); }, []);

  const fetchFamily = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/family/mine`, { headers });
      setFamily(data.family);
      if (data.family && data.family.guardian_id === user?.id) {
        const { data: t } = await axios.get(`${API}/family/trends`, { headers });
        setTrends(t);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const createFamily = async () => {
    if (!familyName.trim()) return;
    setCreating(true);
    try {
      await axios.post(`${API}/family/create`, { name: familyName }, { headers });
      toast.success("Family group created!");
      setFamilyName("");
      fetchFamily();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create family"); }
    finally { setCreating(false); }
  };

  const copyInvite = () => {
    if (!family?.invite_code) return;
    navigator.clipboard.writeText(family.invite_code);
    toast.success("Invite code copied!");
  };

  const regenerateInvite = async () => {
    try {
      const { data } = await axios.post(`${API}/family/invite/regenerate`, {}, { headers });
      setFamily(prev => ({ ...prev, invite_code: data.invite_code }));
      toast.success("New invite code generated!");
    } catch (err) { toast.error("Failed"); }
  };

  const addMember = async () => {
    if (!newMember.username.trim() || !newMember.password.trim() || !newMember.preferred_name.trim()) return;
    setAddingMember(true);
    try {
      await axios.post(`${API}/family/add-member`, newMember, { headers });
      toast.success(`${newMember.preferred_name} added to family!`);
      setNewMember({ username: "", password: "", preferred_name: "", date_of_birth: "", role: "member" });
      setShowAdd(false);
      fetchFamily();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setAddingMember(false); }
  };

  const removeMember = async (memberId) => {
    try {
      await axios.delete(`${API}/family/members/${memberId}`, { headers });
      toast.success("Member removed");
      fetchFamily();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      </AppLayout>
    );
  }

  // No family yet
  if (!family) {
    return (
      <AppLayout>
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto px-6 py-8 space-y-8">
            <div>
              <h1 className="font-heading text-3xl font-medium mb-2">Family</h1>
              <p className="text-muted-foreground">Create a family group or join one with an invite code.</p>
            </div>

            {user?.tier === "family" ? (
              <div className="card-omni p-8 space-y-5">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-6 h-6 text-primary" strokeWidth={1.5} />
                  <h2 className="font-heading text-xl font-medium">Create Your Family Group</h2>
                </div>
                <input
                  data-testid="family-name-input"
                  value={familyName}
                  onChange={e => setFamilyName(e.target.value)}
                  className="input-omni w-full border border-input"
                  placeholder="Family name (e.g., The Smiths)"
                />
                <Button
                  data-testid="create-family-btn"
                  onClick={createFamily}
                  disabled={creating || !familyName.trim()}
                  className="btn-omni bg-primary text-primary-foreground w-full"
                >
                  {creating ? "Creating..." : "Create Family Group"}
                </Button>

                <div className="border-t border-border/50 pt-5">
                  <JoinFamily headers={headers} onJoined={fetchFamily} />
                </div>
              </div>
            ) : (
              <div className="card-omni p-8 text-center">
                <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" strokeWidth={1.5} />
                <h2 className="font-heading text-xl font-medium mb-2">Family Plan Required</h2>
                <p className="text-muted-foreground mb-4">Upgrade to the Family plan to create a family group and monitor your loved ones.</p>
                <JoinFamily headers={headers} onJoined={fetchFamily} />
              </div>
            )}
          </div>
        </div>
      </AppLayout>
    );
  }

  // Has family - show dashboard
  return (
    <AppLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-3xl font-medium mb-1">{family.name}</h1>
              <p className="text-muted-foreground">{family.members?.length || 0} member{family.members?.length !== 1 ? "s" : ""}</p>
            </div>
            {isGuardian && (
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" strokeWidth={1.5} />
                <span className="text-sm text-primary font-medium">Guardian</span>
              </div>
            )}
          </div>

          {/* Invite Code (Guardian Only) */}
          {isGuardian && (
            <div className="card-omni p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-lg font-medium">Invite Code</h2>
                <div className="flex items-center gap-2">
                  <Button data-testid="copy-invite-btn" variant="ghost" onClick={copyInvite} className="rounded-full gap-2 text-sm">
                    <Copy className="w-4 h-4" /> Copy
                  </Button>
                  <Button variant="ghost" onClick={regenerateInvite} className="rounded-full gap-2 text-sm">
                    <RefreshCw className="w-4 h-4" /> New Code
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-2xl">
                <code data-testid="invite-code" className="font-mono text-lg font-semibold text-primary tracking-wider">{family.invite_code}</code>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Share this code with family members so they can join your group.</p>
            </div>
          )}

          {/* Add Member (Guardian Only) */}
          {isGuardian && (
            <div className="card-omni p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-lg font-medium">Members</h2>
                <Button data-testid="add-member-toggle" variant="ghost" onClick={() => setShowAdd(!showAdd)} className="rounded-full gap-2 text-sm">
                  <UserPlus className="w-4 h-4" /> Add Member
                </Button>
              </div>

              {showAdd && (
                <div className="p-5 bg-muted/30 rounded-2xl mb-4 space-y-3" data-testid="add-member-form">
                  <p className="text-sm font-medium">Create an account for a family member</p>
                  <input
                    data-testid="new-member-name"
                    value={newMember.preferred_name}
                    onChange={e => setNewMember(p => ({ ...p, preferred_name: e.target.value }))}
                    className="input-omni w-full border border-input h-12"
                    placeholder="Their name"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      data-testid="new-member-dob"
                      type="date"
                      value={newMember.date_of_birth}
                      onChange={e => setNewMember(p => ({ ...p, date_of_birth: e.target.value }))}
                      className="input-omni w-full border border-input h-12"
                      placeholder="Date of birth"
                      max={new Date().toISOString().slice(0,10)}
                    />
                    <select
                      data-testid="new-member-role"
                      value={newMember.role}
                      onChange={e => setNewMember(p => ({ ...p, role: e.target.value }))}
                      className="input-omni w-full border border-input h-12 rounded-2xl"
                    >
                      <option value="member">Member</option>
                      <option value="child">Child (under 13)</option>
                      <option value="teen">Teen (13-17)</option>
                      <option value="adult">Adult</option>
                    </select>
                  </div>
                  <input
                    data-testid="new-member-username"
                    value={newMember.username}
                    onChange={e => setNewMember(p => ({ ...p, username: e.target.value }))}
                    className="input-omni w-full border border-input h-12"
                    placeholder="Username (min 3 chars)"
                  />
                  <input
                    data-testid="new-member-password"
                    type="password"
                    value={newMember.password}
                    onChange={e => setNewMember(p => ({ ...p, password: e.target.value }))}
                    className="input-omni w-full border border-input h-12"
                    placeholder="Password (min 6 chars)"
                  />

                  {/* Parental Controls Notice */}
                  {(newMember.role === "child" || newMember.role === "teen") && (
                    <div data-testid="parental-controls-notice" className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30">
                      <p className="text-xs font-medium text-amber-800 dark:text-amber-400 mb-1">Parental Controls Enabled</p>
                      <ul className="text-[11px] text-amber-700 dark:text-amber-500 space-y-0.5 list-disc list-inside">
                        {newMember.role === "child" && <>
                          <li>Content filtered for age-appropriate interactions</li>
                          <li>Therapy paths and sensitive topics restricted</li>
                          <li>Voice interactions require guardian approval</li>
                          <li>Activity reports sent to guardian</li>
                        </>}
                        {newMember.role === "teen" && <>
                          <li>Sensitive content moderated with contextual filtering</li>
                          <li>Weekly activity summary available to guardian</li>
                          <li>No financial or career advice without guardian review</li>
                        </>}
                      </ul>
                    </div>
                  )}

                  {/* Legal Disclaimer */}
                  <div data-testid="legal-disclaimer" className="p-3 rounded-xl bg-muted/50 border border-border/30">
                    <p className="text-[10px] text-muted-foreground leading-relaxed">
                      <span className="font-bold">Legal Notice:</span> By creating this account, you confirm you are the legal parent or guardian of this minor (if under 18). You consent to Omni processing their data under your supervision per our <a href="/privacy" className="text-primary underline">Privacy Policy</a> and <a href="/terms" className="text-primary underline">Terms of Service</a>. Under COPPA (US), GDPR-K (EU), and equivalent local regulations, parental consent is required for users under 13. You are responsible for monitoring this member's activity and managing parental controls. Omni does not knowingly collect personal information from children under 13 without verified parental consent.
                    </p>
                  </div>

                  <Button
                    data-testid="add-member-btn"
                    onClick={addMember}
                    disabled={addingMember || !newMember.preferred_name || !newMember.username || !newMember.password || !newMember.date_of_birth}
                    className="btn-omni bg-primary text-primary-foreground w-full"
                  >
                    {addingMember ? "Adding..." : "Create Account & Add"}
                  </Button>
                </div>
              )}

              <div className="space-y-2">
                {family.members?.map(m => (
                  <div key={m.user_id} className="flex items-center justify-between p-3 rounded-2xl hover:bg-muted/30 transition-all">
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold",
                        m.role === "guardian" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                      )}>
                        {m.name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{m.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{m.role}</p>
                      </div>
                    </div>
                    {isGuardian && m.role !== "guardian" && (
                      <button onClick={() => removeMember(m.user_id)} className="text-muted-foreground hover:text-destructive">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Emotional Trends (Guardian Only) */}
          {isGuardian && trends && (
            <div className="space-y-6">
              <h2 className="font-heading text-xl font-medium">Emotional Trends</h2>
              <p className="text-sm text-muted-foreground -mt-4">See how your family is doing emotionally. Private messages are never visible.</p>

              {trends.trends?.map(member => {
                const TrendIcon = TREND_ICONS[member.trend] || Minus;
                const trendColor = TREND_COLORS[member.trend] || "text-muted-foreground";

                return (
                  <div key={member.user_id} data-testid={`trend-${member.user_id}`} className="card-omni p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary">
                          {member.name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <div>
                          <p className="font-medium">{member.name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{member.role}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        {member.avg_score !== null ? (
                          <>
                            <div className="text-center">
                              <p className="font-heading text-xl font-medium">{member.avg_score}</p>
                              <p className="text-[10px] text-muted-foreground">avg mood</p>
                            </div>
                            <div className={cn("flex items-center gap-1", trendColor)}>
                              <TrendIcon className="w-4 h-4" />
                              <span className="text-xs capitalize">{member.trend}</span>
                            </div>
                          </>
                        ) : (
                          <span className="text-xs text-muted-foreground">No data yet</span>
                        )}
                      </div>
                    </div>

                    {member.dominant_emotion && (
                      <p className="text-xs text-muted-foreground mb-3">
                        Dominant emotion: <span className="capitalize font-medium text-foreground">{member.dominant_emotion}</span>
                        {" "}&middot; {member.entries} entries in 14 days
                      </p>
                    )}

                    {member.chart_data?.length > 1 && (
                      <ResponsiveContainer width="100%" height={120}>
                        <AreaChart data={member.chart_data}>
                          <defs>
                            <linearGradient id={`grad-${member.user_id}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                          <YAxis domain={[0, 10]} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={25} />
                          <Tooltip />
                          <Area type="monotone" dataKey="score" stroke="hsl(var(--primary))" fill={`url(#grad-${member.user_id})`} strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Non-guardian view */}
          {!isGuardian && (
            <div className="card-omni p-8 text-center">
              <Users className="w-12 h-12 text-primary/30 mx-auto mb-4" strokeWidth={1.5} />
              <h2 className="font-heading text-xl font-medium mb-2">You&apos;re part of {family.name}</h2>
              <p className="text-muted-foreground">Your guardian can see your emotional trends, but your private conversations remain yours alone.</p>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

function JoinFamily({ headers, onJoined }) {
  const [code, setCode] = useState("");
  const [joining, setJoining] = useState(false);

  const handleJoin = async () => {
    if (!code.trim()) return;
    setJoining(true);
    try {
      const { data } = await axios.post(`${API}/family/join`, { invite_code: code }, { headers });
      toast.success(`Joined ${data.family_name}!`);
      onJoined();
    } catch (err) { toast.error(err.response?.data?.detail || "Invalid code"); }
    finally { setJoining(false); }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium">Have an invite code?</p>
      <div className="flex gap-2">
        <input
          data-testid="join-code-input"
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase())}
          className="input-omni flex-1 border border-input h-12 font-mono"
          placeholder="OMNI-XXXX-XXXX"
        />
        <Button data-testid="join-family-btn" onClick={handleJoin} disabled={joining} className="btn-omni bg-primary text-primary-foreground">
          {joining ? "..." : "Join"}
        </Button>
      </div>
    </div>
  );
}
